import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import Typography from "@mui/material/Typography";
import { FC, useCallback, useEffect, useState } from "react";
import {
  COLOR_GREY_6,
  COLOR_GREY_7,
  COLOR_ERROR,
  COLOR_BLACK,
} from "shared/theme/colors";
import { EyeOffIcon } from "shared/theme/icons/EyeOffIcon";
import { EyeOnIcon } from "shared/theme/icons/EyeOnIcon";
import FieldLabel from "../components/FieldLabel";
import { StyledCharactersCounter, StyledTextField } from "./styled";
import { StyledPasswordAdornment, StyledIconButton } from "./styled";

type ValidationError = {
  key: string;
  message: string;
};

type TextFieldPropsType = TextFieldProps & {
  label?: string;
  labelColor?: string;
  secondaryLabel?: string;
  secondaryLabelColor?: string;
  className?: string;
  additionalSpace?: string;
  charactersLimit?: number;
  size?: "small" | "medium";
  errors?: ValidationError[];
  serverErrors?: ValidationError[];
};

const TextField: FC<TextFieldPropsType> = ({
  label,
  labelColor = COLOR_BLACK,
  secondaryLabel,
  secondaryLabelColor = COLOR_GREY_6,
  ...props
}) => {
  const clientErrors =
    props.errors?.filter((error) => error.key === props.id) ?? [];
  const serverErrors =
    props.serverErrors?.filter((error) => error.key === props.id) ?? [];
  const errors = clientErrors.concat(serverErrors);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [hidePassword, setHidePassword] = useState(true);
  const isPassword = props.type === "password";
  const isError = showErrors && Boolean(errors.length);
  const isCharactersLimitError =
    !!props.charactersLimit &&
    (props.value as string).length > props.charactersLimit;

  const checkErrors = useCallback(() => {
    const isClientErrors = props.errors && props.errors?.length > 0;
    const isServerErrors = props.serverErrors && props.serverErrors?.length > 0;

    if (props.serverErrors === undefined) {
      if (isClientErrors) {
        return setShowErrors(true);
      }
    } else if (isClientErrors || isServerErrors) {
      return setShowErrors(true);
    }
    setShowErrors(false);
  }, [props.errors, props.serverErrors]);

  useEffect(() => {
    checkErrors();
  }, [checkErrors]);

  const passwordAdornment = (
    <StyledPasswordAdornment>
      <InputAdornment position="end">
        <StyledIconButton
          onClick={() => setHidePassword((prev) => !prev)}
          data-testid="hidePass"
        >
          {hidePassword ? (
            <EyeOnIcon color={COLOR_GREY_7} />
          ) : (
            <EyeOffIcon color={COLOR_GREY_7} />
          )}
        </StyledIconButton>
      </InputAdornment>
    </StyledPasswordAdornment>
  );

  const InputProps = isPassword
    ? { ...props.InputProps, endAdornment: passwordAdornment }
    : props.charactersLimit
      ? {
          ...props.InputProps,
          endAdornment:
            (props.value as string).length === 0 ? null : (
              <StyledCharactersCounter>
                <Typography
                  variant="bodySRegular"
                  color={isCharactersLimitError ? COLOR_ERROR : COLOR_BLACK}
                >
                  {(props.value as string).length}/{props.charactersLimit}
                </Typography>
              </StyledCharactersCounter>
            ),
        }
      : props.InputProps;

  const passwordType = hidePassword ? "password" : "text";
  const type = isPassword ? passwordType : props.type;

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      ml={props.additionalSpace}
      className={props.className}
    >
      <FieldLabel
        label={label}
        labelColor={showErrors ? COLOR_ERROR : labelColor}
      />
      {secondaryLabel && (
        <FieldLabel
          label={secondaryLabel}
          fontWeight={400}
          labelColor={showErrors ? COLOR_ERROR : secondaryLabelColor}
        />
      )}
      <StyledTextField
        {...props}
        charactersLimit={props.charactersLimit}
        isCharactersLimitError={isCharactersLimitError}
        type={type}
        error={isError || isCharactersLimitError}
        size={props?.size || "small"}
        label={null}
        InputProps={InputProps}
        onBlur={(e) => {
          if (props.onBlur) {
            props.onBlur(e);
          }
          checkErrors();
        }}
      />
      {showErrors &&
        errors.map((error, idx) => (
          <Box key={idx} display="flex" mt=".5rem">
            <Typography variant="bodyMRegular" color={COLOR_ERROR}>
              {error.message}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

export default TextField;
